import { regionLabel } from '@/utils/regionLabel';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { useRecoilValue } from 'recoil';
import { unreadNotificationCountAtom } from '@/state/notificationState';
import { useInstructorSettlementVisible, usePointEnabled, useSeminarPaymentEnabled } from '@/common/hooks';
import { useMyCouponCount } from '@/components/domain/coupon/useMyCouponCount';
import { NewReportBadge, useSalaryEntry } from '@/pages/SalarySurvey/salarySurveyEntry';
import { claudeTokens } from '@/styles/claudeTokens';
import { UnreadCountBadge, useThreadUnreadTotal } from '@/pages/ApplicationThread/threadEntry';
import { InlineIcon } from '@/components/common/elements/IconLabel';

/**
 * 채용정보 진입 경로 — 헤더/하단탭의 "채용"과 동일하게 전국(0_0) 지역 목록으로 보낸다.
 * (`/recruitments` 는 홈 화면이라 섹션 진입점으로 쓰지 않는다)
 */
/** 알림 설정(브리핑 수신·앱 푸시) — pages/MyPage/NotificationSettingsPage 의 주소(이 메뉴는 그 화면 조각을 불러오지 않게 값만 둔다) */
const NOTIFICATION_SETTINGS_PATH = '/profile/notifications';
const RECRUITMENTS_PATH = '/recruitments/region?category=REGION&tab=region&districts=0_0';

const DRAWER_WIDTH = '316px';

type IconProps = { size?: number; color?: string };

/** 둘러보기/목록에서 공통으로 쓰는 stroke 아이콘 래퍼 (디자인: stroke-width 1.8, accent 색) */
const StrokeIcon = ({ size = 20, color = claudeTokens.color.accentDark, children }: IconProps & { children: React.ReactNode }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        {children}
    </svg>
);

const BellGlyph = (props: IconProps) => (
    <StrokeIcon {...props}><path d="M6 8a6 6 0 0112 0c0 7 3 9 3 9H3s3-2 3-9M10 21a2 2 0 004 0" /></StrokeIcon>
);
const PencilGlyph = (props: IconProps) => (
    <StrokeIcon {...props}><path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" /></StrokeIcon>
);
const ResumeGlyph = (props: IconProps) => (
    <StrokeIcon {...props}><rect x="4" y="3" width="16" height="18" rx="2" /><circle cx="12" cy="9" r="2.5" /><path d="M7 17c1-3 9-3 10 0" /></StrokeIcon>
);
const BriefcaseGlyph = (props: IconProps) => (
    <StrokeIcon {...props}><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2M3 12h18" /></StrokeIcon>
);
const CalendarGlyph = (props: IconProps) => (
    <StrokeIcon {...props}><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 9h18M8 2v4M16 2v4" /></StrokeIcon>
);
const ChatGlyph = (props: IconProps) => (
    <StrokeIcon {...props}><path d="M4 5h16v11H9l-5 4z" /></StrokeIcon>
);
const BulbGlyph = (props: IconProps) => (
    <StrokeIcon {...props}><path d="M9 18h6M10 21h4M12 3a6 6 0 00-4 10.5V16h8v-2.5A6 6 0 0012 3z" /></StrokeIcon>
);
const ChartGlyph = (props: IconProps) => (
    <StrokeIcon {...props}><path d="M4 20h16M6 16V9M11 16V5M16 16v-7" /></StrokeIcon>
);
const NoticeGlyph = (props: IconProps) => (
    <StrokeIcon {...props}><path d="M4 4h16v12H7l-3 3z" /><path d="M8 8h8M8 12h5" /></StrokeIcon>
);
const DocCheckGlyph = (props: IconProps) => (
    <StrokeIcon {...props}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6M9 15l2 2 4-4" /></StrokeIcon>
);
const HeartGlyph = (props: IconProps) => (
    <StrokeIcon {...props}><path d="M20.8 4.6a5.5 5.5 0 00-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 00-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 000-7.8z" /></StrokeIcon>
);
const PenGlyph = (props: IconProps) => (
    <StrokeIcon {...props}><path d="M4 20h4l10-10-4-4L4 16z" /><path d="M13 7l4 4" /></StrokeIcon>
);
const HelpGlyph = (props: IconProps) => (
    <StrokeIcon {...props}><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 015 0c0 1.5-2.5 2-2.5 3.5M12 17h.01" /></StrokeIcon>
);
const ClinicGlyph = (props: IconProps) => (
    <StrokeIcon {...props}><path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6M12 9v4M10 11h4" /></StrokeIcon>
);
const StoreGlyph = (props: IconProps) => (
    <StrokeIcon {...props}><path d="M4 9h16l-1-5H5zM5 9v11h14V9" /><path d="M9 20v-6h6v6" /></StrokeIcon>
);
const ReceiptGlyph = (props: IconProps) => (
    <StrokeIcon {...props}><path d="M6 2h12v20l-3-2-3 2-3-2-3 2z" /><path d="M9 7h6M9 11h6M9 15h4" /></StrokeIcon>
);
const TeacherGlyph = (props: IconProps) => (
    <StrokeIcon {...props}><path d="M22 9L12 4 2 9l10 5z" /><path d="M6 11.5V17c0 1.5 3 3 6 3s6-1.5 6-3v-5.5" /></StrokeIcon>
);
const CardGlyph = (props: IconProps) => (
    <StrokeIcon {...props}><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20M6 15h4" /></StrokeIcon>
);
const CouponGlyph = (props: IconProps) => (
    <StrokeIcon {...props}><path d="M3 7a2 2 0 012-2h14a2 2 0 012 2v3a2 2 0 000 4v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-3a2 2 0 000-4V7z" /><path d="M14 5v14" strokeDasharray="2 2" /></StrokeIcon>
);
const LogoutGlyph = (props: IconProps) => (
    <StrokeIcon {...props} color={props.color ?? claudeTokens.color.text2}><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" /></StrokeIcon>
);
const ChevronGlyph = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C9BFB4" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 6l6 6-6 6" />
    </svg>
);
const CloseGlyph = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={claudeTokens.color.text1} strokeWidth={2} strokeLinecap="round" aria-hidden="true">
        <path d="M6 6l12 12M18 6L6 18" />
    </svg>
);

const Overlay = styled('div', { shouldForwardProp: (prop: string) => prop !== 'isOpen' })<{ isOpen: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: ${props => (props.isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(15, 12, 8, 0.45);
    z-index: 1100;
  }
`;

const Drawer = styled('aside', { shouldForwardProp: (prop: string) => prop !== 'isOpen' })<{ isOpen: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: ${DRAWER_WIDTH};
    max-width: 88vw;
    background: ${claudeTokens.color.bg};
    color: ${claudeTokens.color.text1};
    z-index: 1200;
    transform: translateX(${props => (props.isOpen ? '0' : '100%')});
    transition: transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out;
    /* 닫혀 있을 때는 그림자를 그리지 않는다 — 화면 밖으로 밀어 둔 서랍의 그림자가 오른쪽 끝에 회색 띠로 비친다(흰 화면에서 눈에 띈다) */
    box-shadow: ${props => (props.isOpen ? '-8px 0 28px rgba(20, 20, 40, 0.18)' : 'none')};
    padding-top: env(safe-area-inset-top, 0px);
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px 16px;
  flex-shrink: 0;
`;

const Brand = styled.span`
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.3px;
  color: ${claudeTokens.color.text1};

  &::after {
    content: '';
    display: inline-block;
    width: 0.32em;
    height: 0.32em;
    background: ${claudeTokens.color.accent};
    border-radius: 50%;
    margin-left: 0.08em;
  }
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin: -7px -8px -7px 0;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const DrawerBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  /* 메뉴 목록 끝에서 더 당겨도 스크롤이 뒤 화면으로 새지 않게 */
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 0 12px calc(16px + env(safe-area-inset-bottom, 0px)) 12px;
  display: flex;
  flex-direction: column;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${claudeTokens.color.surface};
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(20, 20, 40, 0.04);
`;

const IdentityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.div<{ square?: boolean }>`
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: ${props => (props.square ? '12px' : '999px')};
  background: ${props => (props.square ? claudeTokens.color.chip.clinical.bg : claudeTokens.color.accentSoft)};
  color: ${props => (props.square ? claudeTokens.color.chip.clinical.fg : claudeTokens.color.accentDark)};
  font-size: ${props => (props.square ? '16px' : '18px')};
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const IdentityText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex-grow: 1;
  min-width: 0;
`;

const IdentityNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const IdentityName = styled.span`
  font-size: 16px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Chip = styled.span<{ tone: 'accent' | 'clinic'; $leadingIcon?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  background: ${props => (props.tone === 'clinic' ? claudeTokens.color.chip.clinical.bg : claudeTokens.color.accentSoft)};
  color: ${props => (props.tone === 'clinic' ? claudeTokens.color.chip.clinical.fg : claudeTokens.color.accentDark)};
  font-size: 11px;
  font-weight: 700;
  /* 앞에 아이콘이 붙으면 아이콘 쪽 안쪽 여백만 4px 줄인다(UI 가이드 4.1) */
  padding: ${props => (props.$leadingIcon ? '2px 7px 2px 3px' : '2px 7px')};
  border-radius: 6px;
`;

const ChipButton = styled.button`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  background: ${claudeTokens.color.accentSoft};
  color: ${claudeTokens.color.accentDark};
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
`;

// 어절 단위로 줄바꿈 — 이메일이 한 줄에 안 들어가면 통째로 다음 줄로, 그래도 길면 그때만 쪼갠다
const IdentitySub = styled.span`
  font-size: 12px;
  color: ${claudeTokens.color.text3};
  line-height: 1.4;
  word-break: keep-all;
  overflow-wrap: anywhere;
`;

/**
 * 프로필 카드 아래 버튼 줄 [알림][치과 정보|내 정보][+ 공고 등록|이력서].
 * 칸은 같은 폭(1fr)이 기본이고, 글자가 한 줄로 안 들어가는 칸만 제 글자 폭까지 넓힌다(max-content 하한)
 * — 2026-09-30 좁은 폰에서 "치과 정 / 보"로 어절 중간이 깨지던 것. 320 폭에서도 세 칸 합이 카드 안에 들어간다.
 */
const QuickGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(max-content, 1fr));
  gap: 8px;
`;

/** 버튼 줄 아이콘 크기 — 둘러보기 타일(20)보다 작게 해 글자 자리를 넓힌다 */
const QUICK_ICON_SIZE = 18;

const QuickAction = styled.button<{ filled?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 44px;
  padding: 0 4px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 13px;
  font-weight: ${props => (props.filled ? 700 : 600)};
  background: ${props => (props.filled ? claudeTokens.color.accent : claudeTokens.color.bg)};
  color: ${props => (props.filled ? '#FFFFFF' : claudeTokens.color.text1)};

  &:active {
    opacity: 0.85;
  }
`;

const QuickBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: ${claudeTokens.color.status.error};
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${claudeTokens.color.text3};
  letter-spacing: 0.3px;
  padding: 14px 4px 6px 4px;
`;

/** 둘러보기 타일 3열 — 치과회원·치과의사는 속닥속닥이 빠져 5개(마지막 줄 2개, 왼쪽 정렬) */
const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  padding: 6px 0 4px 0;
`;

const Tile = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 64px;
  padding: 0 2px;
  background: ${claudeTokens.color.surface};
  border: 1px solid ${claudeTokens.color.border};
  border-radius: 12px;
  cursor: pointer;

  &:active {
    background: ${claudeTokens.color.accentSoft};
  }
`;

const TileLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${claudeTokens.color.text1};
  line-height: 1.2;
  text-align: center;
`;

const RowList = styled.div`
  display: flex;
  flex-direction: column;
  background: ${claudeTokens.color.surface};
  border-radius: 16px;
  padding: 4px 8px;
  box-shadow: 0 2px 8px rgba(20, 20, 40, 0.04);
`;

const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 46px;
  width: 100%;
  padding: 0 4px;
  border: none;
  background: none;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;

  &:active {
    background: ${claudeTokens.color.bg};
  }
`;

const RowLabel = styled.span`
  flex-grow: 1;
  font-size: 15px;
  font-weight: 500;
  color: ${claudeTokens.color.text1};
`;

const RowValue = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${claudeTokens.color.accent};
`;

const GuestCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${claudeTokens.color.accent};
  border-radius: 16px;
  padding: 18px 16px;
  color: #ffffff;
`;

const GuestTitle = styled.div`
  font-size: 16px;
  font-weight: 800;
  word-break: keep-all;
`;

const GuestDesc = styled.div`
  font-size: 12px;
  opacity: 0.92;
  line-height: 1.5;
  word-break: keep-all;
`;

const GuestButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

const GuestPrimaryButton = styled.button`
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  color: ${claudeTokens.color.accentDark};
  font-size: 14px;
  font-weight: 700;
  border: none;
  border-radius: 10px;
  cursor: pointer;
`;

const GuestSecondaryButton = styled.button`
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.7);
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  border-radius: 10px;
  cursor: pointer;
`;

const PromoCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  background: ${claudeTokens.color.surface};
  border-radius: 16px;
  padding: 14px 16px;
  box-shadow: 0 2px 8px rgba(20, 20, 40, 0.04);
  border: 1px solid ${claudeTokens.color.accentSoft};
  cursor: pointer;
  text-align: left;
  width: 100%;
`;

const PromoChip = styled.span`
  display: inline-flex;
  background: ${claudeTokens.color.chip.management.bg};
  color: ${claudeTokens.color.chip.management.fg};
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
`;

const PromoTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${claudeTokens.color.text1};
`;

const PromoDesc = styled.span`
  font-size: 12px;
  color: ${claudeTokens.color.text2};
`;

const Spacer = styled.div`
  flex-grow: 1;
  min-height: 12px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 4px 0 4px;
  font-size: 12px;
  color: ${claudeTokens.color.text3};
`;

const FooterLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const FooterLink = styled.button`
  background: none;
  border: none;
  padding: 2px 0;
  font-size: 12px;
  color: ${claudeTokens.color.text3};
  cursor: pointer;
`;

const FooterSeparator = styled.span`
  color: ${claudeTokens.color.text3};
`;

const LogoutButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 4px 0;
  font-size: 12px;
  font-weight: 600;
  color: ${claudeTokens.color.text2};
  cursor: pointer;
`;

/** 둘러보기 타일 정의 — 세 변형(회원/치과/비로그인) 공통 */
const BROWSE_TILES: { label: string; path: string; icon: React.ReactNode }[] = [
    { label: '채용정보', path: RECRUITMENTS_PATH, icon: <BriefcaseGlyph /> },
    { label: '세미나', path: '/seminars', icon: <CalendarGlyph /> },
    { label: '속닥속닥', path: '/posts', icon: <ChatGlyph /> },
    { label: '전문가 라운지', path: '/expert-lounge', icon: <BulbGlyph /> },
    { label: '급여 실태조사', path: '/salary-surveys', icon: <ChartGlyph /> },
    { label: '공지사항', path: '/notices', icon: <NoticeGlyph /> },
];

/** 계정 카드 둘째 줄 — "직군 · email" (없는 항목은 통째로 생략) */
const buildMemberSubLine = (user: User | null): string => {
    const parts: string[] = [];
    if (user?.userType) parts.push(user.userType);
    if (user?.email) parts.push(user.email);
    return parts.join(' · ');
};

/** 치과 계정 카드 둘째 줄 — "지역 · email" */
const buildClinicSubLine = (user: User | null): string => {
    const region: string = regionLabel(user?.clinics?.city, user?.clinics?.district);
    return [region, user?.email].filter(Boolean).join(' · ');
};

const firstChar = (value: string | undefined | null, fallback: string): string => {
    const trimmed: string = (value ?? '').trim();
    return trimmed.length > 0 ? trimmed.charAt(0) : fallback;
};

/** 서랍 안에서 Tab 으로 이동할 수 있는 요소들 */
const FOCUSABLE_SELECTOR: string = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface MobileDrawerMenuProps {
    /** 열림 여부 — Header 의 mobileMenuOpen 상태 */
    open: boolean;
    /** 닫기(오버레이/X 버튼) */
    onClose: () => void;
    /** 전역 프로필(userAtom). 비로그인 또는 로드 전이면 null */
    user: User | null;
    /** 로그인 여부 — user 로드 전에도 게스트 화면이 깜박이지 않게 별도로 받는다 */
    isLoggedIn: boolean;
    /** 이동 헬퍼 — 이동 + 스크롤 최상단 + 메뉴 닫기 (Header.handleMobileMenuNavigate) */
    onNavigate: (path: string) => void;
    /** 로그아웃 (Header 의 useAuth().logout) */
    onLogout: () => void;
}

/**
 * 모바일 우측 서랍 메뉴.
 *
 * 로그인 상태와 역할에 따라 세 가지 변형을 렌더한다.
 * - 일반 회원: 계정 카드 + 둘러보기 + 내 활동
 * - 치과 회원(ROLE_CLINIC): 치과 카드 + 둘러보기 + 채용 관리
 * - 비로그인: 로그인 유도 카드 + 둘러보기 + 진행 중 캠페인
 *
 * 급여 실태조사 현황은 **서랍이 열릴 때마다** 조회하고, 상위 % 는
 * 응답이 있고 면허 인증된 회원에 한해 기준선 비교 API 를 한 번 추가 호출해 채운다(useSalaryEntry).
 */
const MobileDrawerMenu = ({ open, onClose, user, isLoggedIn, onNavigate, onLogout }: MobileDrawerMenuProps) => {
    const unreadCount = useRecoilValue(unreadNotificationCountAtom);
    const { pointEnabled } = usePointEnabled();
    const { seminarPaymentEnabled, seminarPaymentHistoryVisible } = useSeminarPaymentEnabled();
    // "쿠폰 N장" — 서랍이 열려 있을 때만 센다(실패면 숫자 숨김)
    const couponCount: number | null = useMyCouponCount(open && isLoggedIn && seminarPaymentEnabled && !user?.role?.includes('ROLE_CLINIC'));

    const isClinic: boolean = !!user?.role?.includes('ROLE_CLINIC');
    // 대화(지원 건별 대화방) 안 읽은 수 — 서랍이 열려 있을 때만 센다
    const threadUnread: number = useThreadUnreadTotal(open && isLoggedIn);
    const isInstructor: boolean = !!user?.role?.includes('ROLE_INSTRUCTOR');
    // 강사 정산 — 커리어를 꺼도 판매 기록이 있는 강사에게는 보인다
    const { settlementVisible } = useInstructorSettlementVisible(isInstructor);
    const isMember: boolean = isLoggedIn && !isClinic;

    // 급여 현황은 열 때마다 다시 읽는다(리포트를 본 뒤 N 이 남지 않게). 치과 회원 화면에는 급여 항목이 없어 부르지 않는다.
    // 줄 구성은 마이페이지 '내 활동'과 같은 공용 목록(useSalaryEntry → buildSalaryEntryLinks)
    const { survey, links: salaryLinks } = useSalaryEntry({
        loadCurrent: open && !isClinic,
        canLoadBaseline: isMember && user?.isLicenseVerified === true,
    });

    const drawerRef = useRef<HTMLElement | null>(null);
    /** 메뉴를 열기 직전에 포커스가 있던 곳 — 닫을 때 그 자리로 돌려준다 */
    const lastFocusedRef = useRef<HTMLElement | null>(null);

    // 서랍은 닫혀도 화면 밖에 그대로 남아 있다 → inert 로 포커스·보조기기 탐색에서 제외한다.
    // React 18 은 inert 를 prop 으로 넘기면 경고하므로 DOM 속성으로 직접 건다.
    useEffect(() => {
        drawerRef.current?.toggleAttribute('inert', !open);
    }, [open]);

    // 열리면 서랍 안으로 포커스를 옮기고, 닫히면 열기 전 위치(햄버거 버튼)로 돌려준다
    useEffect(() => {
        if (!open) return undefined;
        lastFocusedRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const drawer: HTMLElement | null = drawerRef.current;
        const firstFocusable: HTMLElement | null = drawer?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? null;
        (firstFocusable ?? drawer)?.focus();
        return () => {
            lastFocusedRef.current?.focus();
        };
    }, [open]);

    // 열린 동안 ESC 로 닫고, Tab 은 서랍 안에서만 돌게 가둔다 (뒤쪽 화면으로 포커스가 새어 나가지 않도록)
    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onClose();
                return;
            }
            if (event.key !== 'Tab') return;
            const drawer: HTMLElement | null = drawerRef.current;
            if (!drawer) return;
            const focusable: HTMLElement[] = Array.from(drawer.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
                .filter((node: HTMLElement) => node.getClientRects().length > 0);
            if (focusable.length === 0) return;
            const first: HTMLElement = focusable[0];
            const last: HTMLElement = focusable[focusable.length - 1];
            const active: Element | null = document.activeElement;
            const insideDrawer: boolean = active instanceof Node && drawer.contains(active);
            if (event.shiftKey && (!insideDrawer || active === first)) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && (!insideDrawer || active === last)) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    const memberSubLine: string = useMemo(() => buildMemberSubLine(user), [user]);
    const clinicSubLine: string = useMemo(() => buildClinicSubLine(user), [user]);
    const unreadLabel: string = unreadCount > 99 ? '99+' : String(unreadCount);

    const participantCount: number | null = survey?.counts?.total ?? null;

    const go = useCallback((path: string) => () => onNavigate(path), [onNavigate]);

    const renderBrowseTiles = () => (
        <>
            <SectionTitle>둘러보기</SectionTitle>
            <TileGrid>
                {BROWSE_TILES.map((tile) => (
                    <Tile key={tile.label} type="button" onClick={go(tile.path)}>
                        {tile.icon}
                        <TileLabel>{tile.label}</TileLabel>
                    </Tile>
                ))}
            </TileGrid>
        </>
    );

    return (
        <>
            <Overlay isOpen={open} onClick={onClose} />
            {/* role="dialog" 는 열렸을 때만 — 닫힌 채로도 달아 두면 스와이프 뒤로가기(SwipeBack)가 "오버레이 열림"으로 오판한다 */}
            <Drawer
                ref={drawerRef}
                isOpen={open}
                aria-hidden={!open}
                role={open ? 'dialog' : undefined}
                aria-modal={open ? true : undefined}
                aria-label={open ? '메뉴' : undefined}
                tabIndex={-1}
            >
                <DrawerHeader>
                    <Brand>명치사</Brand>
                    <IconButton type="button" onClick={onClose} aria-label="메뉴 닫기">
                        <CloseGlyph />
                    </IconButton>
                </DrawerHeader>

                <DrawerBody>
                    {!isLoggedIn && (
                        <GuestCard>
                            <GuestTitle>로그인하고 내 급여 위치를 확인하세요</GuestTitle>
                            <GuestDesc>채용 지원·세미나 신청·속닥속닥 글쓰기는 로그인 후 이용할 수 있어요.</GuestDesc>
                            <GuestButtons>
                                <GuestPrimaryButton type="button" onClick={go('/login')}>로그인</GuestPrimaryButton>
                                <GuestSecondaryButton type="button" onClick={go('/signup')}>회원가입</GuestSecondaryButton>
                            </GuestButtons>
                        </GuestCard>
                    )}

                    {isLoggedIn && isClinic && (
                        <Card>
                            <IdentityRow>
                                <Avatar square>{firstChar(user?.clinics?.name ?? user?.name, '치')}</Avatar>
                                <IdentityText>
                                    <IdentityNameRow>
                                        <IdentityName>{user?.clinics?.name || user?.name || '치과'}</IdentityName>
                                        <Chip tone="clinic">치과 회원</Chip>
                                    </IdentityNameRow>
                                    {clinicSubLine && <IdentitySub>{clinicSubLine}</IdentitySub>}
                                </IdentityText>
                            </IdentityRow>
                            <QuickGrid>
                                <QuickAction type="button" onClick={go('/notifications')}>
                                    <BellGlyph size={QUICK_ICON_SIZE} />
                                    알림
                                    {unreadCount > 0 && <QuickBadge>{unreadLabel}</QuickBadge>}
                                </QuickAction>
                                <QuickAction type="button" onClick={go('/profile/clinic/edit')}>
                                    <PencilGlyph size={QUICK_ICON_SIZE} />
                                    치과 정보
                                </QuickAction>
                                <QuickAction type="button" filled onClick={go('/recruitments/new')}>
                                    <InlineIcon kind="add" size={QUICK_ICON_SIZE} />
                                    공고 등록
                                </QuickAction>
                            </QuickGrid>
                        </Card>
                    )}

                    {isLoggedIn && !isClinic && (
                        <Card>
                            <IdentityRow>
                                <Avatar>{firstChar(user?.nickname ?? user?.name, '명')}</Avatar>
                                <IdentityText>
                                    <IdentityNameRow>
                                        <IdentityName>{user?.nickname || user?.name || '회원'}</IdentityName>
                                        {user?.isLicenseVerified === true ? (
                                            <Chip tone="accent" $leadingIcon><InlineIcon kind="check" size={14} />면허 인증</Chip>
                                        ) : (
                                            <ChipButton type="button" onClick={go('/profile/license-verification')}>
                                                면허 인증하기
                                            </ChipButton>
                                        )}
                                    </IdentityNameRow>
                                    {memberSubLine && <IdentitySub>{memberSubLine}</IdentitySub>}
                                </IdentityText>
                            </IdentityRow>
                            <QuickGrid>
                                <QuickAction type="button" onClick={go('/notifications')}>
                                    <BellGlyph size={QUICK_ICON_SIZE} />
                                    알림
                                    {unreadCount > 0 && <QuickBadge>{unreadLabel}</QuickBadge>}
                                </QuickAction>
                                <QuickAction type="button" onClick={go('/profile/user/edit')}>
                                    <PencilGlyph size={QUICK_ICON_SIZE} />
                                    내 정보
                                </QuickAction>
                                <QuickAction type="button" onClick={go('/resume')}>
                                    <ResumeGlyph size={QUICK_ICON_SIZE} />
                                    이력서
                                </QuickAction>
                            </QuickGrid>
                        </Card>
                    )}

                    {renderBrowseTiles()}

                    {isLoggedIn && !isClinic && (
                        <>
                            <SectionTitle>내 활동</SectionTitle>
                            <RowList>
                                {/* 급여 줄 — 마이페이지 '내 활동'과 같은 공용 목록(내 급여 위치·참여·새 급여 리포트 보기 N) */}
                                {salaryLinks.map((link) => (
                                    <Row key={link.key} type="button" onClick={go(link.path)}>
                                        <ChartGlyph />
                                        <RowLabel>{link.label}</RowLabel>
                                        {link.value && <RowValue>{link.value}</RowValue>}
                                        {link.badge && <NewReportBadge />}
                                        <ChevronGlyph />
                                    </Row>
                                ))}
                                <Row type="button" onClick={go('/apply/history')}>
                                    <DocCheckGlyph />
                                    <RowLabel>지원 내역</RowLabel>
                                    <ChevronGlyph />
                                </Row>
                                <Row type="button" onClick={go('/threads')}>
                                    <ChatGlyph />
                                    <RowLabel>대화</RowLabel>
                                    <UnreadCountBadge count={threadUnread} />
                                    <ChevronGlyph />
                                </Row>
                                <Row type="button" onClick={go('/seminars/history')}>
                                    <CalendarGlyph />
                                    <RowLabel>세미나 이력</RowLabel>
                                    <ChevronGlyph />
                                </Row>
                                <Row type="button" onClick={go('/favorites/clinics')}>
                                    <HeartGlyph />
                                    <RowLabel>관심 치과</RowLabel>
                                    <ChevronGlyph />
                                </Row>
                                <Row type="button" onClick={go('/posts/my')}>
                                    <PenGlyph />
                                    <RowLabel>내가 쓴 글</RowLabel>
                                    <ChevronGlyph />
                                </Row>
                                <Row type="button" onClick={go('/inquiries/my')}>
                                    <HelpGlyph />
                                    <RowLabel>문의 내역</RowLabel>
                                    <ChevronGlyph />
                                </Row>
                                {/* 결제내역은 커리어를 꺼도 결제한 회원에게는 보인다 */}
                                {seminarPaymentHistoryVisible && (
                                    <Row type="button" onClick={go('/payments/history')}>
                                        <CardGlyph />
                                        <RowLabel>결제 내역</RowLabel>
                                        <ChevronGlyph />
                                    </Row>
                                )}
                                {seminarPaymentEnabled && (
                                    <Row type="button" onClick={go('/coupons')}>
                                        <CouponGlyph />
                                        <RowLabel>쿠폰</RowLabel>
                                        {couponCount !== null && couponCount > 0 && <RowValue>{couponCount}장</RowValue>}
                                        <ChevronGlyph />
                                    </Row>
                                )}
                                {isInstructor && (
                                    <Row type="button" onClick={go('/seminar/instructor/edit')}>
                                        <TeacherGlyph />
                                        <RowLabel>강사 정보</RowLabel>
                                        <ChevronGlyph />
                                    </Row>
                                )}
                                {settlementVisible && (
                                    <Row type="button" onClick={go('/seminar/instructor/settlements')}>
                                        <CardGlyph />
                                        <RowLabel>강사 정산</RowLabel>
                                        <ChevronGlyph />
                                    </Row>
                                )}
                                {/* 알림 설정(브리핑 수신·앱 푸시) — 목록 맨 끝 */}
                                <Row type="button" onClick={go(NOTIFICATION_SETTINGS_PATH)}>
                                    <BellGlyph />
                                    <RowLabel>알림 설정</RowLabel>
                                    <ChevronGlyph />
                                </Row>
                            </RowList>
                        </>
                    )}

                    {isLoggedIn && isClinic && (
                        <>
                            <SectionTitle>채용 관리</SectionTitle>
                            <RowList>
                                <Row type="button" onClick={go('/clinic/recruitments')}>
                                    <ClinicGlyph />
                                    <RowLabel>내 채용공고</RowLabel>
                                    <ChevronGlyph />
                                </Row>
                                <Row type="button" onClick={go('/threads')}>
                                    <ChatGlyph />
                                    <RowLabel>대화</RowLabel>
                                    <UnreadCountBadge count={threadUnread} />
                                    <ChevronGlyph />
                                </Row>
                                <Row type="button" onClick={go('/seminars/history')}>
                                    <CalendarGlyph />
                                    <RowLabel>세미나 이력</RowLabel>
                                    <ChevronGlyph />
                                </Row>
                                {/* 속닥속닥 치과의사 공간에 쓴 글 — 개인 회원 '내 활동'의 같은 줄과 같은 모양 */}
                                <Row type="button" onClick={go('/posts/my')}>
                                    <PenGlyph />
                                    <RowLabel>내가 쓴 글</RowLabel>
                                    <ChevronGlyph />
                                </Row>
                                <Row type="button" onClick={go('/inquiries/my')}>
                                    <HelpGlyph />
                                    <RowLabel>문의 내역</RowLabel>
                                    <ChevronGlyph />
                                </Row>
                                {pointEnabled && (
                                    <>
                                        <Row type="button" onClick={go('/products/recruitment')}>
                                            <StoreGlyph />
                                            <RowLabel>상품구매</RowLabel>
                                            <ChevronGlyph />
                                        </Row>
                                        <Row type="button" onClick={go('/purchase-history')}>
                                            <ReceiptGlyph />
                                            <RowLabel>구매내역</RowLabel>
                                            <ChevronGlyph />
                                        </Row>
                                    </>
                                )}
                                {isInstructor && (
                                    <Row type="button" onClick={go('/seminar/instructor/edit')}>
                                        <TeacherGlyph />
                                        <RowLabel>강사 정보</RowLabel>
                                        <ChevronGlyph />
                                    </Row>
                                )}
                                {settlementVisible && (
                                    <Row type="button" onClick={go('/seminar/instructor/settlements')}>
                                        <CardGlyph />
                                        <RowLabel>강사 정산</RowLabel>
                                        <ChevronGlyph />
                                    </Row>
                                )}
                                {/* 알림 설정(브리핑 수신·앱 푸시) — 목록 맨 끝 */}
                                <Row type="button" onClick={go(NOTIFICATION_SETTINGS_PATH)}>
                                    <BellGlyph />
                                    <RowLabel>알림 설정</RowLabel>
                                    <ChevronGlyph />
                                </Row>
                            </RowList>
                        </>
                    )}

                    {!isLoggedIn && (
                        <>
                            <SectionTitle>지금 진행 중</SectionTitle>
                            <PromoCard type="button" onClick={go('/salary-surveys')}>
                                <PromoChip>
                                    {participantCount !== null ? `2026 급여 실태조사 · 참여 ${participantCount}명` : '2026 급여 실태조사'}
                                </PromoChip>
                                <PromoTitle>내 급여, 같은 연차 중 상위 몇 %일까요?</PromoTitle>
                                <PromoDesc>2분 설문 → 작년 조사 기준 내 위치 바로 확인</PromoDesc>
                            </PromoCard>
                        </>
                    )}

                    <Spacer />

                    <Footer>
                        <FooterLinks>
                            <FooterLink type="button" onClick={go('/inquiry')}>문의하기</FooterLink>
                            <FooterSeparator>·</FooterSeparator>
                            <FooterLink type="button" onClick={go('/terms-of-service')}>이용약관</FooterLink>
                            <FooterSeparator>·</FooterSeparator>
                            <FooterLink type="button" onClick={go('/privacy-policy')}>개인정보처리방침</FooterLink>
                        </FooterLinks>
                        {isLoggedIn && (
                            <LogoutButton
                                type="button"
                                onClick={() => {
                                    onLogout();
                                    onClose();
                                }}
                            >
                                <LogoutGlyph size={16} />
                                로그아웃
                            </LogoutButton>
                        )}
                    </Footer>
                </DrawerBody>
            </Drawer>
        </>
    );
};

export default MobileDrawerMenu;
